import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { Client } from '@stomp/stompjs';
import {
  BASE_URL,
  getFreshAccessToken,
  getUserDetails,
  getChats,
  createDirectChat,
  createGroupChat,
  addChatMembers,
  removeChatMember,
  updateChatTitle,
  getChatMessages,
  sendChatMessage,
  reactToChatMessage,
  markChatRead,
  setChatPinned,
  setChatMuted,
} from '../api';
import {
  getCurrentUserId, makeTempId, mergeMessages, upsertMessage, oldestMessageId,
  sortChats, messagePreview, findParticipant,
} from './chatUtils';
import {
  HISTORY_PAGE_SIZE, TYPING_THROTTLE_MS, TYPING_TIMEOUT_MS, MAX_SOCKET_ATTEMPTS,
} from './chatConstants';

const ChatContext = createContext(null);

// Сколько раз за одну сессию сокета пробуем перевыпустить токен по отказу
// сервера. Больше — риск крутиться в цикле, если дело не в токене.
const MAX_AUTH_RETRIES = 2;

// Текст ошибки в ERROR-фрейме приходит свободной строкой, без кода.
const isAuthErrorMessage = (message) =>
  /token|unauthor|аутентиф|авториз|токен/i.test(String(message || ''));

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
};

const initialState = {
  chats: [],
  chatsLoaded: false,
  messagesByChat: {},      // chatId -> ChatMessageDto[] (по возрастанию id)
  hasMoreByChat: {},       // chatId -> есть ли ещё история выше
  typingByChat: {},        // chatId -> { userId: timestamp }
  peerReadByChat: {},      // chatId -> lastReadMessageId собеседника (один курсор на чат)
  activityByUser: {},      // userId -> timestamp последнего события (для «онлайн»)
  peerProfiles: {},        // peerId -> { fullName, role } (имён в ChatSummaryDto нет)
};

const touchActivity = (state, userId) =>
  userId ? { ...state.activityByUser, [userId]: Date.now() } : state.activityByUser;

const reducer = (state, action) => {
  switch (action.type) {
    case 'CHATS_SET': {
      // Курсор прочитанного собеседником инициализируем из списка чатов: по
      // сообщениям, прочитанным до открытия админки, событий уже не будет.
      const peerReadByChat = { ...state.peerReadByChat };
      action.chats.forEach((chat) => {
        const fromServer = chat.peerLastReadMessageId ?? 0;
        peerReadByChat[chat.id] = Math.max(peerReadByChat[chat.id] ?? 0, fromServer);
      });
      return { ...state, chats: sortChats(action.chats), chatsLoaded: true, peerReadByChat };
    }

    case 'PEERS_SET':
      return { ...state, peerProfiles: { ...state.peerProfiles, ...action.profiles } };

    case 'CHAT_UPSERT': {
      const exists = state.chats.some((c) => c.id === action.chat.id);
      const chats = exists
        ? state.chats.map((c) => (c.id === action.chat.id ? { ...c, ...action.chat } : c))
        : [...state.chats, action.chat];
      return { ...state, chats: sortChats(chats) };
    }

    case 'CHAT_REMOVE': {
      const { [action.chatId]: removedMessages, ...messagesByChat } = state.messagesByChat;
      return {
        ...state,
        chats: state.chats.filter((c) => c.id !== action.chatId),
        messagesByChat,
      };
    }

    case 'CHAT_PATCH':
      return {
        ...state,
        chats: sortChats(
          state.chats.map((c) => (c.id === action.chatId ? { ...c, ...action.patch } : c))
        ),
      };

    case 'MESSAGES_SET': {
      const current = state.messagesByChat[action.chatId] || [];
      const next = action.reset
        ? mergeMessages([], action.messages)
        : mergeMessages(current, action.messages);
      return {
        ...state,
        messagesByChat: { ...state.messagesByChat, [action.chatId]: next },
        hasMoreByChat: {
          ...state.hasMoreByChat,
          [action.chatId]:
            action.hasMore != null ? action.hasMore : state.hasMoreByChat[action.chatId],
        },
      };
    }

    case 'MESSAGE_UPSERT': {
      const { message } = action;
      const chatId = message.chatId || action.chatId;
      const current = state.messagesByChat[chatId] || [];
      const next = upsertMessage(current, message);

      // Обновляем превью в списке чатов и счётчик непрочитанных.
      const chats = state.chats.map((chat) => {
        if (chat.id !== chatId) return chat;
        const isNewer =
          !chat.lastMessage ||
          message.id == null ||
          (chat.lastMessage.id ?? -1) <= message.id;
        return {
          ...chat,
          lastMessage: isNewer ? message : chat.lastMessage,
          unreadCount: action.incrementUnread
            ? (chat.unreadCount || 0) + 1
            : chat.unreadCount || 0,
        };
      });

      return {
        ...state,
        messagesByChat: { ...state.messagesByChat, [chatId]: next },
        chats: sortChats(chats),
        activityByUser: touchActivity(state, message.senderId),
      };
    }

    case 'MESSAGE_FAILED': {
      const current = state.messagesByChat[action.chatId] || [];
      return {
        ...state,
        messagesByChat: {
          ...state.messagesByChat,
          [action.chatId]: current.map((m) =>
            m.tempId === action.tempId ? { ...m, failed: true } : m
          ),
        },
      };
    }

    case 'REACTIONS_SET': {
      const current = state.messagesByChat[action.chatId] || [];
      return {
        ...state,
        messagesByChat: {
          ...state.messagesByChat,
          [action.chatId]: current.map((m) =>
            m.id === action.messageId ? { ...m, reactions: action.reactions } : m
          ),
        },
      };
    }

    // Курсор собеседника не должен ехать назад — события могут прийти не по порядку.
    case 'READ_RECEIPT':
      return {
        ...state,
        peerReadByChat: {
          ...state.peerReadByChat,
          [action.chatId]: Math.max(
            state.peerReadByChat[action.chatId] ?? 0,
            action.lastReadMessageId ?? 0
          ),
        },
        activityByUser: touchActivity(state, action.userId),
      };

    case 'CHAT_READ_LOCAL':
      return {
        ...state,
        chats: state.chats.map((c) => (c.id === action.chatId ? { ...c, unreadCount: 0 } : c)),
      };

    case 'TYPING_SET':
      return {
        ...state,
        typingByChat: {
          ...state.typingByChat,
          [action.chatId]: {
            ...(state.typingByChat[action.chatId] || {}),
            [action.userId]: Date.now(),
          },
        },
        activityByUser: touchActivity(state, action.userId),
      };

    case 'TYPING_PRUNE': {
      const now = Date.now();
      const typingByChat = {};
      let changed = false;
      Object.entries(state.typingByChat).forEach(([chatId, users]) => {
        const alive = Object.entries(users).filter(([, ts]) => now - ts < TYPING_TIMEOUT_MS);
        if (alive.length !== Object.keys(users).length) changed = true;
        if (alive.length > 0) typingByChat[chatId] = Object.fromEntries(alive);
      });
      return changed ? { ...state, typingByChat } : state;
    }

    default:
      return state;
  }
};

export const ChatProvider = ({ children, enabled = true }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [connected, setConnected] = useState(false);
  const [socketError, setSocketError] = useState(null);
  // 'connecting' | 'connected' | 'reconnecting' | 'failed'
  const [connectionState, setConnectionState] = useState('connecting');
  const [socketEpoch, setSocketEpoch] = useState(0);

  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const attemptsRef = useRef(0);
  // Бэкенд отверг токен — на следующей попытке берём новый, не дожидаясь exp.
  const forceTokenRefreshRef = useRef(false);
  const authRetriesRef = useRef(0);
  const clientRef = useRef(null);
  const connectedRef = useRef(false);
  const activeChatIdRef = useRef(null);
  const knownChatIdsRef = useRef(new Set());
  const readCursorRef = useRef({});     // chatId -> последний отправленный lastReadMessageId
  const typingSentAtRef = useRef({});   // chatId -> время последней публикации typing
  const [activeChatId, setActiveChatIdState] = useState(null);

  const setActiveChatId = useCallback((chatId) => {
    activeChatIdRef.current = chatId;
    setActiveChatIdState(chatId);
  }, []);

  // ── REST-загрузки ──────────────────────────────────────────────
  const loadChats = useCallback(async () => {
    try {
      const chats = await getChats();
      dispatch({ type: 'CHATS_SET', chats: Array.isArray(chats) ? chats : [] });
      return chats;
    } catch (e) {
      // Список не загрузился (нет сети/бэкенда) — снимаем лоадер и показываем пустое состояние.
      dispatch({ type: 'CHATS_SET', chats: [] });
      return [];
    }
  }, []);

  const loadHistory = useCallback(async (chatId, { reset = false } = {}) => {
    if (!chatId) return [];
    const messages = await getChatMessages(chatId, { limit: HISTORY_PAGE_SIZE });
    dispatch({
      type: 'MESSAGES_SET',
      chatId,
      messages,
      reset,
      hasMore: messages.length >= HISTORY_PAGE_SIZE,
    });
    return messages;
  }, []);

  // Keyset-пагинация вверх: beforeId — самый старый загруженный id.
  const loadOlder = useCallback(async (chatId) => {
    const current = state.messagesByChat[chatId] || [];
    const beforeId = oldestMessageId(current);
    if (beforeId == null) return [];
    const messages = await getChatMessages(chatId, { beforeId, limit: HISTORY_PAGE_SIZE });
    dispatch({
      type: 'MESSAGES_SET',
      chatId,
      messages,
      hasMore: messages.length >= HISTORY_PAGE_SIZE,
    });
    return messages;
  }, [state.messagesByChat]);

  // Пересобрать одно сообщение (например, когда протухла presigned-ссылка).
  const refetchMessage = useCallback(async (chatId, messageId) => {
    try {
      const messages = await getChatMessages(chatId, { beforeId: messageId + 1, limit: 1 });
      const fresh = messages.find((m) => m.id === messageId) || messages[0];
      if (fresh) dispatch({ type: 'MESSAGE_UPSERT', message: fresh, chatId });
      return fresh || null;
    } catch {
      return null;
    }
  }, []);

  // ── WebSocket ──────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return undefined;
    if (!localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
      return undefined;
    }

    const client = new Client({
      // Нативный WebSocket: подключается сразу, без предварительного GET /ws/info.
      // Схема берётся из BASE_URL — ws:// на локалке, wss:// в проде.
      brokerURL: `${BASE_URL.replace(/^http/, 'ws')}/ws`,
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      debug: () => {},
    });

    // Аутентификация проходит на STOMP-фрейме CONNECT, а не на handshake, и
    // заголовок пересобирается перед КАЖДОЙ попыткой: за время сессии access
    // token истекает, а со старым заголовком переподключения бесконечно
    // получали «невалидный токен» — помогала только перезагрузка страницы.
    client.beforeConnect = async () => {
      const token = await getFreshAccessToken({ force: forceTokenRefreshRef.current });
      forceTokenRefreshRef.current = false;
      if (!token) {
        // Сессии больше нет: сокет не поднимаем, выход в login сделает REST-слой.
        setConnectionState('failed');
        client.deactivate();
        return;
      }
      client.connectHeaders = { Authorization: `Bearer ${token}` };
    };

    const safeParse = (body) => {
      try {
        return JSON.parse(body);
      } catch {
        return null;
      }
    };

    client.onConnect = () => {
      connectedRef.current = true;
      attemptsRef.current = 0;
      authRetriesRef.current = 0;
      setConnected(true);
      setConnectionState('connected');
      setSocketError(null);

      client.subscribe('/user/queue/messages', (frame) => {
        const message = safeParse(frame.body);
        if (!message) return;
        const isOwn = String(message.senderId) === String(currentUserId);
        const isActive = activeChatIdRef.current === message.chatId;
        dispatch({
          type: 'MESSAGE_UPSERT',
          message,
          incrementUnread: !isOwn && (!isActive || document.hidden),
        });
        // Сообщение из чата, которого ещё нет в списке — подтягиваем список.
        if (!knownChatIdsRef.current.has(message.chatId)) loadChats();
      });

      client.subscribe('/user/queue/reactions', (frame) => {
        const update = safeParse(frame.body);
        if (!update) return;
        dispatch({
          type: 'REACTIONS_SET',
          chatId: update.chatId,
          messageId: update.messageId,
          reactions: update.reactions || [],
        });
      });

      // Событие приходит всем участникам, включая того, кто читал.
      client.subscribe('/user/queue/read', (frame) => {
        const receipt = safeParse(frame.body);
        if (!receipt) return;

        if (String(receipt.userId) === String(currentUserId)) {
          // Свой же курсор с другого устройства — гасим бейдж непрочитанных.
          dispatch({ type: 'CHAT_READ_LOCAL', chatId: receipt.chatId });
          return;
        }

        dispatch({
          type: 'READ_RECEIPT',
          chatId: receipt.chatId,
          userId: receipt.userId,
          lastReadMessageId: receipt.lastReadMessageId,
        });
      });

      client.subscribe('/user/queue/typing', (frame) => {
        const event = safeParse(frame.body);
        if (!event?.chatId || !event?.userId) return;
        if (String(event.userId) === String(currentUserId)) return;
        dispatch({ type: 'TYPING_SET', chatId: event.chatId, userId: event.userId });
      });

      client.subscribe('/user/queue/errors', (frame) => {
        setSocketError(frame.body || 'Ошибка чата');
      });

      // После (ре)коннекта досылок не будет — перечитываем список и историю.
      loadChats();
      if (activeChatIdRef.current) {
        loadHistory(activeChatIdRef.current, { reset: true }).catch(() => {});
      }
    };

    // Ограничиваем переподключения: если /ws закрыт для неаутентифицированных
    // запросов (SockJS не умеет слать Authorization на /ws/info и handshake),
    // бесконечный цикл раз в 5 секунд только шумит в логах.
    client.onWebSocketClose = () => {
      connectedRef.current = false;
      setConnected(false);
      attemptsRef.current += 1;

      if (attemptsRef.current >= MAX_SOCKET_ATTEMPTS) {
        setConnectionState('failed');
        client.deactivate();
      } else {
        setConnectionState('reconnecting');
      }
    };
    client.onStompError = (frame) => {
      const message = frame?.headers?.message || '';
      // Токен мог быть отозван до истечения exp: просим новый и не тратим на
      // это попытки переподключения — иначе сокет «умирал» до перезагрузки.
      if (isAuthErrorMessage(message) && authRetriesRef.current < MAX_AUTH_RETRIES) {
        authRetriesRef.current += 1;
        forceTokenRefreshRef.current = true;
        attemptsRef.current = 0;
        setConnectionState('reconnecting');
        setSocketError(null);
        return;
      }
      setSocketError(message || 'Ошибка соединения');
    };

    attemptsRef.current = 0;
    authRetriesRef.current = 0;
    setConnectionState('connecting');
    client.activate();
    clientRef.current = client;

    return () => {
      connectedRef.current = false;
      setConnected(false);
      clientRef.current = null;
      client.deactivate();
    };
  }, [enabled, currentUserId, loadChats, loadHistory, socketEpoch]);

  // Ручная повторная попытка после того, как автопереподключения закончились.
  const reconnect = useCallback(() => {
    attemptsRef.current = 0;
    authRetriesRef.current = 0;
    // Ручная попытка после отказа — начинаем с заведомо свежего токена.
    forceTokenRefreshRef.current = true;
    setSocketError(null);
    setConnectionState('connecting');
    setSocketEpoch((epoch) => epoch + 1);
  }, []);

  // Пока вкладка была скрыта (или пропадала сеть), попытки могли закончиться.
  // Возвращаемся к работе — поднимаем сокет сам, без перезагрузки страницы.
  useEffect(() => {
    if (!enabled) return undefined;
    const retryIfDead = () => {
      if (document.hidden) return;
      if (connectionState === 'failed') reconnect();
    };
    document.addEventListener('visibilitychange', retryIfDead);
    window.addEventListener('online', retryIfDead);
    window.addEventListener('focus', retryIfDead);
    return () => {
      document.removeEventListener('visibilitychange', retryIfDead);
      window.removeEventListener('online', retryIfDead);
      window.removeEventListener('focus', retryIfDead);
    };
  }, [enabled, connectionState, reconnect]);

  useEffect(() => {
    knownChatIdsRef.current = new Set(state.chats.map((chat) => chat.id));
  }, [state.chats]);

  // Имён собеседников в ChatSummaryDto нет — дотягиваем профили по peerId
  // (запрос к деталям пользователя кэшируется в api-слое).
  const requestedPeersRef = useRef(new Set());
  useEffect(() => {
    const unknown = state.chats
      // Если имя пришло в participants — запрашивать профиль незачем.
      .filter((chat) => chat.peerId && !findParticipant(chat, chat.peerId)?.fullName)
      .map((chat) => chat.peerId)
      .filter((peerId) => !state.peerProfiles[peerId] && !requestedPeersRef.current.has(peerId));
    if (unknown.length === 0) return;

    unknown.forEach((peerId) => requestedPeersRef.current.add(peerId));
    Promise.all(
      unknown.map(async (peerId) => {
        try {
          const user = await getUserDetails(peerId);
          return [peerId, { fullName: user?.fullName || user?.username || null, role: user?.role || null }];
        } catch {
          return null;
        }
      })
    ).then((entries) => {
      const profiles = Object.fromEntries(entries.filter(Boolean));
      if (Object.keys(profiles).length > 0) dispatch({ type: 'PEERS_SET', profiles });
    });
  }, [state.chats, state.peerProfiles]);

  // Профиль, который уже известен (например, выбран в диалоге «новый чат»).
  const rememberPeer = useCallback((peerId, profile) => {
    if (!peerId) return;
    requestedPeersRef.current.add(peerId);
    dispatch({ type: 'PEERS_SET', profiles: { [peerId]: profile } });
  }, []);

  // Гасим индикаторы «печатает» через 3 секунды после последнего события.
  useEffect(() => {
    const timer = setInterval(() => dispatch({ type: 'TYPING_PRUNE' }), 1000);
    return () => clearInterval(timer);
  }, []);

  // Первичная загрузка списка чатов (даже если сокет ещё не поднялся).
  useEffect(() => {
    if (enabled) loadChats();
  }, [enabled, loadChats]);

  // ── Действия ───────────────────────────────────────────────────
  const publish = useCallback((destination, body) => {
    if (!connectedRef.current || !clientRef.current?.connected) return false;
    clientRef.current.publish({ destination, body: JSON.stringify(body) });
    return true;
  }, []);

  /**
   * Отправка: сокет, если подключён, иначе REST тем же телом.
   * В UI сообщение появляется сразу с временным id; серверная версия
   * приходит по сокету и заменяет оптимистичную (матч по отправителю+тексту+времени).
   */
  const sendMessage = useCallback(async ({
    chatId, content, fileId, replyToId, durationMs, waveform, messageType = 'TEXT', localPreview,
  }) => {
    if (!chatId) return null;
    if (!content && !fileId) return null;

    const tempId = makeTempId();
    const optimistic = {
      id: null,
      tempId,
      chatId,
      senderId: currentUserId,
      messageType,
      content: content || null,
      attachment: localPreview || null,
      replyTo: null,
      durationMs: durationMs ?? null,
      waveform: waveform ?? null,
      createdAt: new Date().toISOString(),
      reactions: [],
      pending: true,
    };
    dispatch({ type: 'MESSAGE_UPSERT', message: optimistic, chatId });

    const payload = { chatId };
    if (content) payload.content = content;
    if (fileId) payload.fileId = fileId;
    if (replyToId) payload.replyToId = replyToId;
    if (durationMs != null) payload.durationMs = durationMs;
    if (waveform) payload.waveform = waveform;

    if (publish('/app/chat.send', payload)) return tempId;

    try {
      const saved = await sendChatMessage(chatId, payload);
      dispatch({ type: 'MESSAGE_UPSERT', message: saved, chatId });
    } catch {
      dispatch({ type: 'MESSAGE_FAILED', chatId, tempId });
    }
    return tempId;
  }, [currentUserId, publish]);

  // Toggle: локально ничего не помним, ответом приходит полный агрегат.
  const toggleReaction = useCallback(async (chatId, messageId, emoji) => {
    if (publish('/app/chat.react', { messageId, emoji })) return;
    try {
      const update = await reactToChatMessage(messageId, emoji);
      dispatch({
        type: 'REACTIONS_SET',
        chatId: update.chatId || chatId,
        messageId: update.messageId ?? messageId,
        reactions: update.reactions || [],
      });
    } catch {
      /* пользователю не показываем — реакция просто не применится */
    }
  }, [publish]);

  // Курсор прочитанного двигается только вперёд.
  const markRead = useCallback((chatId, lastReadMessageId) => {
    if (!chatId || lastReadMessageId == null) return;
    const previous = readCursorRef.current[chatId] ?? -1;
    if (lastReadMessageId <= previous) return;
    readCursorRef.current[chatId] = lastReadMessageId;

    dispatch({ type: 'CHAT_READ_LOCAL', chatId });
    if (!publish('/app/chat.read', { chatId, lastReadMessageId })) {
      markChatRead(chatId, lastReadMessageId).catch(() => {});
    }
  }, [publish]);

  const sendTyping = useCallback((chatId) => {
    if (!chatId) return;
    const now = Date.now();
    if (now - (typingSentAtRef.current[chatId] || 0) < TYPING_THROTTLE_MS) return;
    typingSentAtRef.current[chatId] = now;
    publish('/app/chat.typing', { chatId });
  }, [publish]);

  // peer — либо UUID, либо объект пользователя из поиска (тогда сразу знаем имя).
  const openDirectChat = useCallback(async (peer) => {
    const peerId = typeof peer === 'string' ? peer : peer?.id;
    if (!peerId) return null;
    if (typeof peer === 'object') {
      rememberPeer(peerId, {
        fullName: peer.fullName || peer.username || null,
        role: peer.role || null,
      });
    }
    const chat = await createDirectChat(peerId);
    dispatch({ type: 'CHAT_UPSERT', chat });
    return chat;
  }, [rememberPeer]);

  // ── Группы ─────────────────────────────────────────────────────
  const createGroup = useCallback(async (title, memberIds) => {
    const chat = await createGroupChat(title, memberIds);
    dispatch({ type: 'CHAT_UPSERT', chat });
    return chat;
  }, []);

  // Эндпоинты состава отвечают 200 без тела — состав и title перечитываем из /api/chats.
  // Системное сообщение о действии придёт по сокету и добавится в ленту само.
  const addMembers = useCallback(async (chatId, memberIds) => {
    await addChatMembers(chatId, memberIds);
    await loadChats();
  }, [loadChats]);

  const removeMember = useCallback(async (chatId, userId) => {
    await removeChatMember(chatId, userId);
    await loadChats();
  }, [loadChats]);

  const renameChat = useCallback(async (chatId, title) => {
    await updateChatTitle(chatId, title);
    await loadChats();
  }, [loadChats]);

  // Выход из группы — удаление самого себя; чат сразу убираем из списка.
  const leaveChat = useCallback(async (chatId) => {
    await removeChatMember(chatId, currentUserId);
    dispatch({ type: 'CHAT_REMOVE', chatId });
    if (activeChatIdRef.current === chatId) setActiveChatId(null);
    loadChats();
  }, [currentUserId, loadChats, setActiveChatId]);

  const togglePinned = useCallback(async (chatId, value) => {
    dispatch({ type: 'CHAT_PATCH', chatId, patch: { pinned: value } });
    try {
      await setChatPinned(chatId, value);
    } catch {
      dispatch({ type: 'CHAT_PATCH', chatId, patch: { pinned: !value } });
    }
  }, []);

  const toggleMuted = useCallback(async (chatId, value) => {
    dispatch({ type: 'CHAT_PATCH', chatId, patch: { muted: value } });
    try {
      await setChatMuted(chatId, value);
    } catch {
      dispatch({ type: 'CHAT_PATCH', chatId, patch: { muted: !value } });
    }
  }, []);

  const unreadTotal = useMemo(
    () => state.chats.reduce((sum, chat) => sum + (chat.unreadCount || 0), 0),
    [state.chats]
  );

  // В UI отдаём чаты уже с именами собеседников.
  const enrichedChats = useMemo(
    () => state.chats.map((chat) => ({
      ...chat,
      peerName: chat.peerId ? state.peerProfiles[chat.peerId]?.fullName || null : null,
      peerRole: chat.peerId ? state.peerProfiles[chat.peerId]?.role || null : null,
    })),
    [state.chats, state.peerProfiles]
  );

  const value = useMemo(() => ({
    ...state,
    chats: enrichedChats,
    connected,
    connectionState,
    reconnect,
    socketError,
    currentUserId,
    activeChatId,
    setActiveChatId,
    unreadTotal,
    loadChats,
    loadHistory,
    loadOlder,
    refetchMessage,
    sendMessage,
    toggleReaction,
    markRead,
    sendTyping,
    openDirectChat,
    createGroup,
    addMembers,
    removeMember,
    renameChat,
    leaveChat,
    togglePinned,
    toggleMuted,
    lastMessagePreview: messagePreview,
  }), [
    state, enrichedChats, connected, connectionState, reconnect, socketError, currentUserId, activeChatId, setActiveChatId, unreadTotal,
    loadChats, loadHistory, loadOlder, refetchMessage, sendMessage, toggleReaction, markRead,
    sendTyping, openDirectChat, createGroup, addMembers, removeMember, renameChat, leaveChat,
    togglePinned, toggleMuted,
  ]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatProvider;
